import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Campo, Selector } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { usePermiso } from '../sesion/ProveedorDeSesion';

export interface Rubro {
  readonly id: string;
  readonly nombre: string;
  readonly padreId: string | null;
  readonly activa: boolean;
  readonly articulos: number;
}

/** Un rubro con la profundidad a la que cuelga, para poder sangrarlo en la tabla. */
interface RubroEnElArbol extends Rubro {
  readonly profundidad: number;
}

/**
 * El árbol aplanado: cada rubro debajo de su padre, con la profundidad para sangrarlo.
 *
 * Se recorre desde las raíces y no se ordena por nombre al final, porque ordenar por nombre después
 * de armar el árbol lo desarma: un hijo terminaría arriba de su padre.
 */
export function aplanarElArbol(rubros: readonly Rubro[]): readonly RubroEnElArbol[] {
  const salida: RubroEnElArbol[] = [];
  const bajar = (padreId: string | null, profundidad: number): void => {
    for (const rubro of rubros.filter((fila) => fila.padreId === padreId)) {
      salida.push({ ...rubro, profundidad });
      bajar(rubro.id, profundidad + 1);
    }
  };
  bajar(null, 0);

  // Los que quedaron afuera son los que cuelgan de un rubro que la lista no trae. No debería pasar
  // —la clave foránea lo impide—, pero esconderlos sería peor que mostrarlos: un rubro invisible es
  // un rubro que nadie puede arreglar.
  for (const rubro of rubros) {
    if (!salida.some((fila) => fila.id === rubro.id)) salida.push({ ...rubro, profundidad: 0 });
  }
  return salida;
}

/**
 * Cómo se nombra un rubro dentro de un selector: «— Oro 18k» colgando de «Oro».
 *
 * Está afuera del JSX porque las rayas son sangría, no texto: un `<option>` no admite estilos de
 * indentación en todos los navegadores, así que la jerarquía se dibuja con caracteres. Y está en
 * una función porque el lint —regla 3— rechaza cualquier literal adentro del JSX, que es
 * exactamente lo que hay que hacer con los literales que *sí* son texto.
 */
export function etiquetaSangrada(nombre: string, profundidad: number): string {
  return `${'— '.repeat(profundidad)}${nombre}`;
}

/**
 * Rubros: cómo se agrupan los artículos.
 *
 * ## Por qué es una pantalla y no un campo del artículo
 *
 * Porque **un artículo puede estar en varios** —lo pidió el dueño del producto—: un anillo es
 * «Anillos» y es «Oro 18k» a la vez, y las dos cosas se usan para buscar, para poner precio y para
 * pagar comisión. Un campo obliga a elegir una, y elegir una es perder la otra.
 *
 * ## Por qué es un árbol
 *
 * Ya lo era en la base desde E2. Lo que cambia acá es que se puede ver y armar: «Oro» con «Oro 18k»
 * adentro es como piensa quien vende, y lo alternativo —cien rubros sueltos en una lista— deja de
 * servir a los treinta.
 */
export function Rubros(): React.ReactElement {
  const { t } = useTranslation();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();
  const puedeEditar = usePermiso('CATALOGO.CATEGORIA.EDITAR');

  const [abierto, setAbierto] = useState(false);
  const [nombre, setNombre] = useState('');
  const [padreId, setPadreId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [corrigiendo, setCorrigiendo] = useState<Rubro | null>(null);
  const [edicion, setEdicion] = useState({ nombre: '', padreId: '' });
  const [errorDeCorreccion, setErrorDeCorreccion] = useState<string | null>(null);

  const consulta = useQuery({
    queryKey: ['categorias'],
    queryFn: () => pedir<Rubro[]>('/categorias'),
  });

  const lista = consulta.data ?? [];
  const enElArbol = aplanarElArbol(lista);

  const crear = useMutation({
    mutationFn: () =>
      pedir<{ id: string }>('/categorias', {
        method: 'POST',
        cuerpo: {
          nombre: nombre.trim(),
          ...(padreId === '' ? {} : { padreId }),
        },
      }),
    onSuccess: async () => {
      setAbierto(false);
      setNombre('');
      setPadreId('');
      setError(null);
      await cola.invalidateQueries({ queryKey: ['categorias'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const corregir = useMutation({
    mutationFn: () => {
      const original = corrigiendo;
      const cambios: Record<string, unknown> = {};
      if (original !== null) {
        if (edicion.nombre.trim() !== original.nombre) cambios.nombre = edicion.nombre.trim();
        // Vaciar el selector es «sacalo de donde está», que se manda como nulo. No mandarlo sería
        // «no lo toques», que es otra cosa.
        if (edicion.padreId !== (original.padreId ?? '')) {
          cambios.padreId = edicion.padreId === '' ? null : edicion.padreId;
        }
      }
      return pedir<{ id: string }>(`/categorias/${original?.id ?? ''}`, {
        method: 'PATCH',
        cuerpo: cambios,
      });
    },
    onSuccess: async () => {
      setCorrigiendo(null);
      setErrorDeCorreccion(null);
      await cola.invalidateQueries({ queryKey: ['categorias'] });
    },
    onError: (fallo: unknown) => {
      setErrorDeCorreccion(textoDeError(fallo));
    },
  });

  const cambiarEstado = useMutation({
    mutationFn: (fila: Rubro) =>
      pedir<{ id: string }>(`/categorias/${fila.id}`, {
        method: 'PATCH',
        cuerpo: { activa: !fila.activa },
      }),
    onSuccess: async () => {
      await cola.invalidateQueries({ queryKey: ['categorias'] });
    },
  });

  const columnas: Columna<RubroEnElArbol>[] = [
    {
      clave: 'nombre',
      titulo: t('rubros.nombre'),
      contenido: (fila) => (
        <span style={{ paddingLeft: `${String(fila.profundidad * 1.25)}rem` }}>{fila.nombre}</span>
      ),
    },
    {
      clave: 'articulos',
      titulo: t('rubros.articulos'),
      // Cuántos artículos tiene es lo que dice si un rubro sirve o quedó de una carga vieja, y es
      // lo que hay que mirar antes de desactivarlo.
      contenido: (fila) => <span className="cifra">{fila.articulos}</span>,
    },
    {
      clave: 'estado',
      titulo: t('rubros.estado'),
      contenido: (fila) => (
        <Insignia tono={fila.activa ? 'bien' : 'neutro'}>
          {fila.activa ? t('rubros.activo') : t('rubros.inactivo')}
        </Insignia>
      ),
    },
    {
      clave: 'acciones',
      titulo: '',
      contenido: (fila) =>
        !puedeEditar ? null : (
          <div className="flex justify-end gap-1">
            <Boton
              onClick={() => {
                setCorrigiendo(fila);
                setEdicion({ nombre: fila.nombre, padreId: fila.padreId ?? '' });
                setErrorDeCorreccion(null);
              }}
            >
              {t('acciones.corregir')}
            </Boton>
            {/* No hay borrar: un rubro con artículos adentro dejaría los artículos apuntando a la
                nada. Se desactiva, que es lo que de verdad se quiere decir. */}
            <Boton
              cargando={cambiarEstado.isPending}
              onClick={() => {
                cambiarEstado.mutate(fila);
              }}
            >
              {fila.activa ? t('rubros.desactivar') : t('rubros.activar')}
            </Boton>
          </div>
        ),
    },
  ];

  return (
    <>
      <Encabezado
        titulo={t('rubros.titulo')}
        descripcion={t('rubros.descripcion')}
        acciones={
          puedeEditar ? (
            <Boton
              tono="principal"
              onClick={() => {
                setAbierto(true);
                setError(null);
              }}
            >
              {t('rubros.nuevo')}
            </Boton>
          ) : undefined
        }
      />

      <Tarjeta>
        {consulta.isPending ? <Cargando /> : null}
        {consulta.isError ? (
          <Problema
            error={consulta.error}
            reintentar={() => {
              void consulta.refetch();
            }}
          />
        ) : null}
        {consulta.isSuccess ? (
          <Tabla
            columnas={columnas}
            filas={enElArbol}
            claveDeFila={(fila) => fila.id}
            vacio={<Vacio titulo={t('rubros.vacio')} descripcion={t('rubros.paraQueSirven')} />}
          />
        ) : null}
      </Tarjeta>

      <Dialogo
        abierto={abierto}
        titulo={t('rubros.nuevo')}
        onCerrar={() => {
          setAbierto(false);
          setError(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={crear.isPending}
            disabled={nombre.trim() === ''}
            onClick={() => {
              crear.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <Campo
            etiqueta={t('rubros.nombre')}
            obligatorio
            value={nombre}
            maxLength={120}
            onChange={(evento) => {
              setNombre(evento.target.value);
            }}
          />
          <Selector
            etiqueta={t('rubros.cuelgaDe')}
            value={padreId}
            ayuda={t('rubros.ayudaCuelgaDe')}
            onChange={(evento) => {
              setPadreId(evento.target.value);
            }}
          >
            <option value="">{t('rubros.sinPadre')}</option>
            {enElArbol.map((fila) => (
              <option key={fila.id} value={fila.id}>
                {etiquetaSangrada(fila.nombre, fila.profundidad)}
              </option>
            ))}
          </Selector>
          {error === null ? null : (
            <p role="alert" className="text-apoyo text-mal">
              {error}
            </p>
          )}
        </div>
      </Dialogo>

      <Dialogo
        abierto={corrigiendo !== null}
        titulo={t('rubros.corregir')}
        onCerrar={() => {
          setCorrigiendo(null);
          setErrorDeCorreccion(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={corregir.isPending}
            disabled={edicion.nombre.trim() === ''}
            onClick={() => {
              corregir.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <Campo
            etiqueta={t('rubros.nombre')}
            obligatorio
            value={edicion.nombre}
            maxLength={120}
            onChange={(evento) => {
              setEdicion((previo) => ({ ...previo, nombre: evento.target.value }));
            }}
          />
          <Selector
            etiqueta={t('rubros.cuelgaDe')}
            value={edicion.padreId}
            onChange={(evento) => {
              setEdicion((previo) => ({ ...previo, padreId: evento.target.value }));
            }}
          >
            <option value="">{t('rubros.sinPadre')}</option>
            {/* El propio rubro no se ofrece como padre de sí mismo. Los descendientes sí aparecen
                y el servidor los rechaza: filtrarlos acá pediría recorrer el árbol dos veces para
                evitar un error que el servidor explica bien. */}
            {enElArbol
              .filter((fila) => fila.id !== corrigiendo?.id)
              .map((fila) => (
                <option key={fila.id} value={fila.id}>
                  {etiquetaSangrada(fila.nombre, fila.profundidad)}
                </option>
              ))}
          </Selector>
          {errorDeCorreccion === null ? null : (
            <p role="alert" className="text-apoyo text-mal">
              {errorDeCorreccion}
            </p>
          )}
        </div>
      </Dialogo>
    </>
  );
}
